<template>
  <div v-if="requestSent" class="text-center">
    <p data-test="forgot-password-sent">
      If an account exists for this email, we've sent a password reset link. It expires in {{ PASSWORD_RESET_LINK_TTL_MINUTES }} minutes.
    </p>
    <v-btn
      variant="text"
      color="primary"
      class="auth-link-btn"
      data-test="forgot-password-back"
      @click="emit('click:back')"
    >
      Login to my account
    </v-btn>
  </div>
  <v-form
    v-else
    ref="form"
    @submit.prevent="handleSubmit"
  >
    <p class="mb-3">Enter the email address associated with your account, and we'll send you a link to reset your password.</p>
    <v-text-field
      v-model="email"
      label="E-mail"
      :rules="EMAIL_RULES"
      variant="outlined"
      placeholder="Email Address"
      autocapitalize="none"
      type="email"
    />
    <div class="text-center mt-4">
      <div
        v-if="captchaRequired"
        :id="CAPTCHA_CONTAINER_ID"
        ref="captcha"
        class="captcha-container d-flex justify-center mb-4"
      />
      <v-btn
        class="primary mb-5"
        :loading="requestLoading"
        type="submit"
      >
        Send Reset Link
      </v-btn>
      <div>
        <v-btn
          variant="text"
          color="primary"
          class="auth-link-btn"
          data-test="forgot-password-back"
          @click="emit('click:back')"
        >
          Login to my account
        </v-btn>
      </div>
    </div>
  </v-form>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useTheme } from 'vuetify';

import { authService } from '@/bundles/Auth/factory/authServiceFactory';
import { PASSWORD_RESET_LINK_TTL_MINUTES } from '@/bundles/Auth/consts';
import { isPentestEmail } from '@/bundles/Auth/helpers';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { renderCaptcha, resetCaptcha, validateCaptcha } from '@/plugins/captcha';
import { emailPattern } from '@/utils/validation';
// Aliased: a bare `VForm` binding would shadow Vuetify's <v-form> in the template.
import type { VForm as VFormInstance } from '@/types/common';

interface IProps {
  initialEmail?: string;
}

interface IEmits {
  'click:back': [];
}

const props = withDefaults(defineProps<IProps>(), {
  initialEmail: '',
});
const emit = defineEmits<IEmits>();

const CAPTCHA_CONTAINER_ID = 'captcha-forgot-password-container';
const EMAIL_RULES: ReadonlyArray<(value: string) => boolean | string> = Object.freeze([
  (value: string) => !!value || 'E-mail is required',
  (value: string) => emailPattern.test(value) || 'E-mail must be valid',
]);

const { addNotification } = useNotifications();
const theme = useTheme();

const form = useTemplateRef<VFormInstance>('form');
const captcha = useTemplateRef<HTMLElement>('captcha');

const email = ref(props.initialEmail);
const requestLoading = ref(false);
const requestSent = ref(false);

const normalizedEmail = computed(() => email.value.toLowerCase().trim());
const captchaRequired = computed(() => !isPentestEmail(normalizedEmail.value));

const renderFormCaptcha = () => {
  nextTick(() => renderCaptcha(CAPTCHA_CONTAINER_ID, theme.current.value.dark ? 'dark' : 'light'));
};

watch(captchaRequired, (value) => {
  if (value) {
    renderFormCaptcha();
  }
});

onMounted(renderFormCaptcha);

const handleSubmit = async () => {
  const { valid } = await form.value!.validate();
  if (!valid) {
    return;
  }

  const captchaResponse = captchaRequired.value ? validateCaptcha() : true;
  if (!captchaResponse) {
    captcha.value?.classList.add('captcha-container--error');
    return;
  }

  requestLoading.value = true;
  try {
    await authService.forgotPasswordRequest({
      email: normalizedEmail.value,
      ...(typeof captchaResponse === 'string' && { response: captchaResponse }),
    });
    requestSent.value = true;
  } catch (error: any) {
    addNotification({ ...error, status: 0 });
    resetCaptcha();
  } finally {
    requestLoading.value = false;
  }
};
</script>
